package com.arenaproplay.admin.data.auth

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class LoginRequest(
    @SerialName("email") val email: String,
    @SerialName("password") val password: String
)

@Serializable
data class RefreshTokenRequest(
    @SerialName("refreshToken") val refreshToken: String
)

@Serializable
data class AuthTokens(
    @SerialName("accessToken") val accessToken: String,
    @SerialName("refreshToken") val refreshToken: String,
    @SerialName("expiresIn") val expiresIn: Long? = null
)

@Serializable
data class AuthUser(
    @SerialName("id") val id: String,
    @SerialName("email") val email: String,
    @SerialName("name") val name: String? = null,
    @SerialName("fullName") val fullName: String? = null,
    @SerialName("username") val username: String? = null,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("role") val role: String? = null,
    @SerialName("roles") val roles: List<String> = emptyList(),
) {
    /** Backend ships RBAC roles in `roles`. Falls back to legacy `role` field
     *  and finally to false for clients that don't send anything. */
    val isAdmin: Boolean
        get() =
            roles.any { it.equals("ADMIN", ignoreCase = true) } ||
                role?.equals("ADMIN", ignoreCase = true) == true
}

@Serializable
data class LoginResponseData(
    @SerialName("accessToken") val accessToken: String? = null,
    @SerialName("refreshToken") val refreshToken: String? = null,
    @SerialName("user") val user: AuthUser? = null,
)

@Serializable
data class LoginResponse(
    // Backend wraps the actual auth payload in `data` (envelope: { code, data, errors }).
    @SerialName("data") val data: LoginResponseData? = null,
    // Direct fields kept for the dev-only mockAdminSession path and any
    // future endpoint that returns the payload at the top level.
    @SerialName("tokens") val tokens: AuthTokens? = null,
    @SerialName("accessToken") val accessToken: String? = null,
    @SerialName("refreshToken") val refreshToken: String? = null,
    @SerialName("user") val user: AuthUser? = null,
) {
    fun resolveTokens(): AuthTokens? {
        data?.let {
            val at = it.accessToken
            val rt = it.refreshToken
            if (at != null && rt != null) return AuthTokens(at, rt)
        }
        if (tokens != null) return tokens
        val at = accessToken ?: return null
        val rt = refreshToken ?: return null
        return AuthTokens(at, rt)
    }

    fun resolveUser(): AuthUser? = data?.user ?: user
}
